import React, { useState, useRef, RefObject, useEffect } from 'react';
import { FaLock, FaUnlockAlt } from 'react-icons/fa';

import { BundlesPageJson, BundleStatus, CourierJson } from 'types/bundleTypes';
import { useFetchBundlesWithStatuses } from 'hooks/useFetchBundlesWithStatuses';
import { useFetchCouriers } from 'hooks/useFetchCouriers';

import Input from 'components/inputs/Input';
import Map from 'components/maps/Map';
import CouriersList from './components/CouriersList';

import './Couriers.scss';
import { CourierStatus } from 'types/bundleTypes';
import { ReqEnum } from 'const/requestStatus';
import { Region } from '../../types/orderTypes';

const statusListInDelivery = [BundleStatus.Proposed, BundleStatus.Accepted];
const currentPage = 1;

export default function Couriers({ region }: { region: Region }) {
  const FILTER_FIELDS: (keyof CourierJson)[] = [
    'id',
    'firstName',
    'lastName',
    'email',
    'phoneNumber',
    'companyName',
  ];

  const stickyDiv: RefObject<HTMLDivElement> = useRef(null);
  const stickyMapRef: RefObject<HTMLDivElement> = useRef(null);

  const [courierFilter, setCourierFilter] = useState('');

  // TODO these filter states should be reflected in the map?
  const [courierStatusAvailable, setCourierStatusAvailable] = useState(true);
  const [courierStatusUnavailable, setCourierStatusUnavailable] =
    useState(false);
  const [courierStatusInactive, setCourierStatusInactive] = useState(false);
  const [courierStatusOffline, setCourierStatusOffline] = useState(false);
  const [courierStatusLocked, setCourierStatusLocked] = useState(false);

  const [lockMapWhenScroll, setLockMapWhenScroll] = useState(false);
  const [stickyMap, setStickyMap] = useState(false);

  const [, bundlesInDelivery, fetchBundlesStatus] = useFetchBundlesWithStatuses(
    currentPage,
    statusListInDelivery,
    region
  );

  const [couriers, fetchCouriersStatus] = useFetchCouriers();

  useEffect(() => {
    const handleScroll = () => {
      if (!lockMapWhenScroll) {
        return;
      }
      if (window.innerHeight < 700) {
        setStickyMap(false);
      }

      if (window.pageYOffset >= stickyDiv.current.offsetTop) {
        setStickyMap(true);
        stickyMapRef.current.classList.add('stickToTop');
      } else {
        setStickyMap(false);
        stickyMapRef.current.classList.remove('stickToTop');
      }
    };
    handleScroll();
  }, [lockMapWhenScroll, region]);

  const filteredCouriers = couriers.filter(courier =>
    FILTER_FIELDS.some(
      field =>
        courier[field] &&
        String(courier[field]).toLowerCase().includes(courierFilter)
    )
  );

  const couriersWithLocation = filteredCouriers.filter(
    courier => courier.latitude && courier.longitude
  );

  function makeCourierMarker(
    courier: CourierJson,
    bundlesInDelivery: BundlesPageJson
  ) {
    const courierName = `${courier.firstName}${
      courier.lastName ? ` ${courier.lastName}` : ''
    }`;

    const bundle = bundlesInDelivery.items.find(
      bundle => Number(bundle.assignedCourierId) === Number(courier.id)
    );

    const markerObject = {
      lat: courier.latitude,
      lng: courier.longitude,
      icon: {
        type: 'porterbuddy',
        info: {
          id: courier.id,
          name: courierName,
          bundle: bundle
            ? `<a href=/bundles/${bundle.id}>${bundle.id}</a>`
            : 'N/A',
          email: courier.email,
          phone: `${courier.phoneCode} ${courier.phoneNumber}`,
          xOfYCompleted: 'N/A',
        },
      },
    };

    return markerObject;
  }

  const courierLocations =
    bundlesInDelivery &&
    fetchBundlesStatus === ReqEnum.SUCCESS &&
    couriersWithLocation
      .filter(courier => courier.status === CourierStatus.Available)
      .map(courier => makeCourierMarker(courier, bundlesInDelivery));

  function CouriersTable({
    filteredCouriers,
    fetchCouriersStatus,
  }: {
    filteredCouriers: CourierJson[];
    fetchCouriersStatus: ReqEnum;
  }) {
    const filterOffline = courierStatusOffline;
    const filterInactive = courierStatusInactive;
    const filterAvailable = courierStatusAvailable;
    const filterUnavailable = courierStatusUnavailable;
    const filterLocked = courierStatusLocked;

    const couriers = filteredCouriers.filter(courier => {
      return (
        (filterOffline &&
          courier.status === CourierStatus.Offline &&
          !courier.isLocked) ||
        (filterInactive &&
          courier.status === CourierStatus.Inactive &&
          !courier.isLocked) ||
        (filterAvailable &&
          courier.status === CourierStatus.Available &&
          !courier.isLocked) ||
        (filterUnavailable &&
          courier.status === CourierStatus.Unavailable &&
          !courier.isLocked) ||
        (filterLocked && courier.isLocked)
      );
    });

    return (
      <table style={{ width: '100%' }}>
        <thead className="courier__list-item-header">
          <tr>
            <td>ID</td>
            <td>Name</td>
            <td>Company</td>
            <td>Phone</td>
            <td>Email</td>
            <td>Rating</td>
            <td>Status</td>
            <td>ETA</td>
            <td>Travel mode</td>
          </tr>
        </thead>
        <CouriersList
          couriers={couriers}
          fetchCouriersStatus={fetchCouriersStatus}
        />
      </table>
    );
  }

  function CouriersStatusFilters() {
    return (
      <div className="courier__filter">
        Show couriers with status:
        <div className="courier__filter-options">
          <label className="courier__filter-label" htmlFor="availableCheck">
            <input
              id="availableCheck"
              type="checkbox"
              onChange={() =>
                setCourierStatusAvailable(!courierStatusAvailable)
              }
              checked={courierStatusAvailable}
            />
            Available
          </label>
          <label className="courier__filter-label" htmlFor="unavailableCheck">
            <input
              id="unavailableCheck"
              type="checkbox"
              onChange={() =>
                setCourierStatusUnavailable(!courierStatusUnavailable)
              }
              checked={courierStatusUnavailable}
            />
            Unavailable
          </label>
          <label className="courier__filter-label" htmlFor="inactiveCheck">
            <input
              id="inactiveCheck"
              type="checkbox"
              onChange={() => setCourierStatusInactive(!courierStatusInactive)}
              checked={courierStatusInactive}
            />
            Inactive
          </label>
          <label className="courier__filter-label" htmlFor="offlineCheck">
            <input
              id="offlineCheck"
              type="checkbox"
              onChange={() => setCourierStatusOffline(!courierStatusOffline)}
              checked={courierStatusOffline}
            />
            Offline
          </label>
          <label className="courier__filter-label" htmlFor="isLockedCheck">
            <input
              id="isLockedCheck"
              type="checkbox"
              onChange={() => setCourierStatusLocked(!courierStatusLocked)}
              checked={courierStatusLocked}
            />
            Locked
          </label>
        </div>
      </div>
    );
  }

  return (
    <>
      <div ref={stickyDiv} />
      <div className="couriers">
        <div>
          <div>
            {lockMapWhenScroll ? (
              <FaLock
                style={{
                  width: 30,
                  height: 30,
                  color: '#b3b3b3',
                  cursor: 'pointer',
                }}
                onClick={() => setLockMapWhenScroll(!lockMapWhenScroll)}
              />
            ) : (
              <FaUnlockAlt
                style={{
                  width: 30,
                  height: 30,
                  color: '#b3b3b3',
                  cursor: 'pointer',
                }}
                onClick={() => setLockMapWhenScroll(!lockMapWhenScroll)}
              />
            )}
          </div>
        </div>
        <div
          style={{
            height: stickyMap && lockMapWhenScroll ? 500 : 0,
          }}
        />
        <div ref={stickyMapRef}>
          <Map
            height={500}
            width={'100%'}
            markers={courierLocations !== null ? courierLocations : []}
          />
        </div>
        <div style={{ height: 30 }} />
        <Input
          style={{
            display: 'inline',
            width: '100%',
            borderRadius: 5,
            fontSize: '1.5rem',
            paddingTop: 8,
            paddingBottom: 8,
          }}
          placeholder="Filter..."
          onChange={event => setCourierFilter(event.target.value.toLowerCase())}
          value={courierFilter}
        />
        <CouriersStatusFilters />
        <CouriersTable
          filteredCouriers={filteredCouriers}
          fetchCouriersStatus={fetchCouriersStatus}
        />
      </div>
    </>
  );
}
